import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Alert, Box, Card, CardContent, Tooltip } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getBasicFilteringOptions,
  getClearOperationalAreaModel,
} from "../../common/EntitiesManagement";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import BackgroundForegroundColorPicker from "../../components/common/BackgroundForegroundColorPicker";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function OperationalArea() {
  const {
    operationalAreaModel,
    setOperationalAreaModel,
    operationalAreaData,
    OPERATIONAL_AREA_API,
    config,
    pageIndexOperativeArea,
    setPageIndexOperativeArea,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    setBasicFilterOptions,
    basicFilterData,
  } = useGlobalContext();
  const { id } = useParams();
  const [operationalAreaDataFiltered, setOperationalAreaDataFiltered] =
    useState(operationalAreaData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const operationalDataObj = operationalAreaData.Data.find(
        (operationalAreaObj) => operationalAreaObj.Id === Number(id)
      );
      setOperationalAreaModel(operationalDataObj);
    }
  }, [id]);

  useEffect(() => {
    setOperationalAreaDataFiltered(operationalAreaData.Data);
  }, [operationalAreaData]);

  const validateOperationResult = (result, isDelete) => {
    setOperationalAreaModel(getClearOperationalAreaModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([OPERATIONAL_AREA_API, config]);
    if (isDelete) {
      if (
        pageIndexOperativeArea !== 0 &&
        operationalAreaData.Data.length === 1
      ) {
        setPageIndexOperativeArea(pageIndexOperativeArea - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${OPERATIONAL_AREA_API}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${OPERATIONAL_AREA_API}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${OPERATIONAL_AREA_API}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleColorChange = (e) => {
    setOperationalAreaModel({
      ...operationalAreaModel,
      [e.target.name]: e.target.value,
    });
  };

  const getComponent = () => {
    return (
      <AddOrEditBasicEntity
        nameMandatory
        customWidth="100%"
        model={operationalAreaModel}
        setModel={setOperationalAreaModel}
        customComponent={
          <Box className="flex flex-col gap-2">
            <Alert sx={{ width: "100%" }} severity="info" className="mb-2">
              <label className="text-xs">
                {localize.color_explanation.replace(
                  "%1s",
                  localize.color_explanation_complement_operative_area
                )}
              </label>
            </Alert>

            <BackgroundForegroundColorPicker
              backgroundValue={operationalAreaModel.BackgroundColor}
              foregroundValue={operationalAreaModel.ForegroundColor}
              onChange={handleColorChange}
            />
          </Box>
        }
      />
    );
  };

  const disableSaveChanges = () =>
    !operationalAreaModel?.Name ||
    operationalAreaModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.description,
      localize.background_color,
      localize.foreground_color,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.operational_areas}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={OPERATIONAL_AREA_API}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="operational_area"
        />
      )}

      {showEditModal && (
        <ModalBackground
          hasRequiredValues
          isDrawerMode
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_operational_area}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(operationalAreaModel);
            setShowEditModal(false);
            setOperationalAreaModel(getClearOperationalAreaModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setOperationalAreaModel(getClearOperationalAreaModel());
          }}
          Component={getComponent()}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.operational_area}
            subtitle={localize.operational_area_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="operational_area_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                hasRequiredValues
                isDrawerMode
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_operational_area}
                onClickToSubmit={() => {
                  handleCreateRequest(operationalAreaModel);
                  setShowAddModal(false);
                  setOperationalAreaModel(getClearOperationalAreaModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setOperationalAreaModel(getClearOperationalAreaModel());
                }}
                Component={getComponent()}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={operationalAreaModel.Name}
          entityType={localize.operational_area}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setOperationalAreaModel(getClearOperationalAreaModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(operationalAreaModel.Id);
            setShowDeleteModal(false);
            setOperationalAreaModel(getClearOperationalAreaModel());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexOperativeArea}
            setPageIndex={setPageIndexOperativeArea}
            dataArray={operationalAreaData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          operationalAreaData.Data?.length !== 0
            ? operationalAreaDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setOperationalAreaModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setOperationalAreaModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Description} />,
                  <Tooltip title={item.BackgroundColor}>
                    <button
                      className="btn btn-sm background_color_representation"
                      style={{
                        backgroundColor: `${item.BackgroundColor}`,
                      }}
                    />
                  </Tooltip>,
                  <Tooltip title={item.ForegroundColor}>
                    <p
                      className="text-2xl foreground_color_representation"
                      style={{ color: `${item.ForegroundColor}` }}
                    >
                      Aa
                    </p>
                  </Tooltip>,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(7)]
        }
      />
    </div>
  );
}
